/**
 * The recommendation engine (§8, FR-27, FR-28).
 *
 * Implements the composite score:
 *
 *   final = w1·Z(vor) + w2·Z(rank) + w3·Z(scarcity) + w4·Z(roster fit)
 *           + w5·Z(wait cost) − w6·Z(bye conflict)
 *
 * where `Z` standardises a feature across the available pool. **Every feature
 * is standardised, and that is load-bearing**: it is what makes a weight mean a
 * share of influence rather than an arbitrary multiplier on whatever units the
 * feature happened to be in. See `standardize` at the bottom of this file for
 * what went wrong when only one of them was.
 *
 * Three behaviours from §8 are load-bearing and are tested directly:
 *
 *  - The `w5·Z(wait cost)` term is what produces "don't reach — he'll be back
 *    to you". A player who will obviously still be there is worth less *now*
 *    than an equal player who will not be. It is a product of two things —
 *    how likely you are to lose him, and how much worse your fallback is — so
 *    that neither certainty nor scarcity alone can drive a pick.
 *  - The `w4·Z(roster fit)` term prices a player against *your* lineup rather
 *    than against the league's, and is deliberately zero when your roster has
 *    nothing to say yet, so it cannot quietly restate the value term.
 *  - Setting `w2 = 1` and every other weight to 0 must degenerate cleanly to
 *    "just follow the active ranking list". That is the escape hatch for a user
 *    who trusts their source more than they trust this tool, and it has to work
 *    exactly, not approximately. Standardising is a strictly increasing
 *    transform, so it preserves that property exactly.
 *
 * Pure module — no I/O.
 */

import type {
  AdpRow,
  DraftState,
  EngineConfig,
  LeagueConfig,
  Player,
  PlayerId,
  Position,
  Projection,
  Recommendation,
  ScoreComponents,
  Valuation,
} from './types.ts';
import {
  byeConflictPenalty,
  computeRosterNeed,
  computeScarcity,
  deriveTiers,
  replacementBaselines,
  rosterFitPoints,
  valueOverReplacement,
  type PoolEntry,
  type RosterNeed,
  type ScarcityState,
} from './valuation.ts';
import { availabilityAt, buildSurvivorModel, waitingPicks } from './availability.ts';
import { LOOKAHEAD_CANDIDATES, lookaheadValuer } from './lookahead.ts';
import type { PlayerRankView } from './blend.ts';
import { applySpecialistRecommendationPolicy, specialistPolicyFor } from './specialistPolicy.ts';

export interface EngineInput {
  league: LeagueConfig;
  draft: DraftState;
  config: EngineConfig;
  /** Every player still on the board, with ranks and projections attached. */
  available: PoolEntry[];
  /** Blended rank view per player, for the rank_score term. */
  rankView: Map<PlayerId, PlayerRankView>;
  /** Total number of ranked players, for percentile normalisation. */
  rankedCount: number;
  adp: Map<PlayerId, AdpRow>;
  projections: Map<PlayerId, Projection>;
  playerOf: (id: PlayerId) => Player | undefined;
  /** Tiers from the active source; derived from projections when absent. */
  tiers?: Map<PlayerId, number | null>;
}

export interface EngineOutput {
  valuations: Map<PlayerId, Valuation>;
  /** Top 5 with breakdowns (FR-27). */
  top: Recommendation[];
  /** A transparent policy that explains why a position was held out of `top`. */
  recommendation_policy: RecommendationPolicy | null;
  scarcity: ScarcityState;
  need: RosterNeed;
  baselines: ReturnType<typeof replacementBaselines>;
  tiers: Map<PlayerId, number>;
  /** The user's next two picks, for the UI header and the availability term. */
  my_next_pick: number | null;
  my_pick_after: number | null;
}

export interface RecommendationPolicy {
  reason: 'one-qb-redraft-depth';
  deferred_position: 'QB';
  /** Core positions with an unfilled starter or eligible flex slot. */
  open_core_positions: Position[];
  deferred_player_count: number;
}

const CORE_OFFENSIVE_POSITIONS: readonly Position[] = ['RB', 'WR', 'TE'];

export function runEngine(input: EngineInput): EngineOutput {
  const { league, draft, config, available, rankView, adp, playerOf } = input;
  const weights = config.weights;

  const positionOf = (id: PlayerId): Position | null => playerOf(id)?.position ?? null;

  const baselines = replacementBaselines(available, league, config.baseline_method);

  // Tiers: prefer what the source said, fall back to gap detection on points.
  const derived = deriveTiers(available);
  const tiers = new Map<PlayerId, number>();
  for (const entry of available) {
    const id = entry.player.player_id_sleeper;
    const fromSource = input.tiers?.get(id);
    tiers.set(id, fromSource ?? derived.get(id) ?? 1);
  }
  const tiersNullable = new Map<PlayerId, number | null>(tiers);

  const scarcity = computeScarcity(
    draft.picks,
    available,
    tiersNullable,
    12,
    positionOf,
  );
  const need = computeRosterNeed(draft.my_roster, league, positionOf);
  const specialistPolicy = specialistPolicyFor(draft, league, need);

  const recommendationPolicy = oneQbRedraftDepthPolicy({
    league,
    draft,
    config,
    available,
    need,
    positionOf,
  });

  const { next, after } = waitingPicks(
    draft.my_draft_slot,
    league.teams,
    league.rounds,
    league.draft_type,
    draft.current_pick_no,
  );

  // What a player is worth to *this* roster, over and above what he is worth to
  // an empty one. Zero on an open roster; negative once a position fills up.
  const fitOf = rosterFitPoints(draft.my_roster, league, positionOf, baselines);

  // What you would expect to get at each position if you passed and waited.
  // This is what turns the timing term from a flat "he'll probably last"
  // discount into a real opportunity cost, and it is also the board the
  // lookahead reads its follow-up picks off.
  const survivors = buildSurvivorModel(
    available.map((e) => ({
      player_key: e.player.player_id_sleeper,
      position: e.position,
      points: e.proj_points,
      rank: e.overall_rank,
    })),
    adp,
    draft,
    league.teams,
    league.rounds,
    league.draft_type,
  );

  // --- Raw features -------------------------------------------------------
  //
  // Collected first, standardised second, combined third. The three-pass shape
  // is what makes the weights mean what they say: see `standardize` below.

  const raw = available.map((entry) => {
    const id = entry.player.player_id_sleeper;
    const vor = valueOverReplacement(entry.proj_points, entry.position, baselines);

    // rank_score is the player's percentile in the active/blended ordering,
    // 1.0 for the top of the board. Falls back to positional rank when the
    // player is unranked, so an unranked player is never scored as if he were
    // rank 1.
    const view = rankView.get(id);
    const rank_score = view
      ? view.blended_percentile
      : clamp01(1 - (entry.overall_rank ?? input.rankedCount) / Math.max(input.rankedCount, 1));

    const adpRow = adp.get(id);
    const availabilityKnown = next !== null && adpRow !== undefined;
    const availability_prob = !availabilityKnown
      ? 0
      : availabilityAt({
          adp: adpRow!.adp,
          stdev: adpRow!.stdev,
          times_drafted: adpRow!.times_drafted,
          target_pick: next!,
          current_pick: draft.current_pick_no,
        }).probability;

    // The cost of waiting: how much better he is than what you would expect at
    // this position next turn, times the chance you would actually lose him.
    //
    // Both halves are load-bearing. A player who will certainly be gone but is
    // no better than his replacement costs nothing to lose; a player who is far
    // better than his replacement but certain to last costs nothing to wait on.
    // Only the product is a reason to spend *this* pick, which is why the old
    // flat `−w · P(survives)` discount could never express the thing the term
    // exists for.
    const fallback = survivors.expectedBest(entry.position);
    const edgeOverWait = fallback === undefined ? 0 : Math.max(0, entry.proj_points - fallback);
    const wait_cost = availabilityKnown ? (1 - availability_prob) * edgeOverWait : 0;

    return {
      entry,
      id,
      vor,
      rank_score,
      scarcity: scarcity.score[entry.position] ?? 0,
      need: fitOf(entry.position, entry.proj_points),
      wait_cost,
      bye_penalty: byeConflictPenalty(entry.player, draft.my_roster, league, playerOf),
      availability_prob,
      availabilityKnown,
    };
  });

  // --- Standardise --------------------------------------------------------

  const z = {
    vor: standardize(raw.map((r) => r.vor)),
    rank: standardize(raw.map((r) => r.rank_score)),
    scarcity: standardize(raw.map((r) => r.scarcity)),
    need: standardize(raw.map((r) => r.need)),
    wait: standardize(raw.map((r) => r.wait_cost)),
    bye: standardize(raw.map((r) => r.bye_penalty)),
  };

  // --- Lookahead ----------------------------------------------------------
  //
  // Evaluated second because it needs the other six to decide who is worth
  // evaluating: only the best candidates can reach the top five this term
  // exists to reorder, and running it over the whole pool would be work nobody
  // sees. Everyone outside that slice keeps a standardised zero — the neutral
  // value, not a penalty — so the cut cannot invent an ordering of its own.

  const provisional = raw.map((_, i) =>
    weights.vor * z.vor[i]! +
    weights.rank * z.rank[i]! +
    weights.scarcity * z.scarcity[i]! +
    weights.need * z.need[i]! +
    weights.availability * z.wait[i]! -
    weights.bye * z.bye[i]!,
  );

  const shortlist = raw
    .map((_, i) => i)
    .sort((a, b) => provisional[b]! - provisional[a]!)
    .slice(0, LOOKAHEAD_CANDIDATES);

  const lookaheadRaw = new Array<number>(raw.length).fill(0);
  const zLookahead = new Array<number>(raw.length).fill(0);

  if (weights.lookahead > 0 && survivors.targetPick !== null) {
    const valueOf = lookaheadValuer({
      league,
      roster: draft.my_roster,
      positionOf,
      baselines,
      survivors,
    });

    for (const i of shortlist) {
      const r = raw[i]!;
      lookaheadRaw[i] = valueOf(r.id, r.entry.position, r.entry.proj_points);
    }

    // Scaled against the *value* term's spread rather than its own.
    //
    // This term is denominated in points, exactly like VOR, so the two share a
    // natural scale — and using it is what keeps the term honest about how much
    // it actually knows. Standardising against its own spread was tried and is
    // wrong: in round one every pick leaves you a near-identical follow-up, so
    // the values span less than a point on a base of 220, and forcing that to
    // unit variance turned pure noise into a confident reordering (the best
    // running back on the board fell seven places for the crime of being the
    // best running back, since taking him leaves a marginally worse one behind).
    //
    // Against the VOR spread it fades to nothing when every pick leaves the
    // same follow-up, and grows to a real signal exactly when it should — when
    // one choice fills your last open slot and another does not.
    const spread = stdev(
      raw.map((r) => r.vor),
      mean(raw.map((r) => r.vor)),
    );
    const centre = mean(shortlist.map((i) => lookaheadRaw[i]!));
    if (spread > 0) {
      for (const i of shortlist) {
        const scaled = (lookaheadRaw[i]! - centre) / spread;
        zLookahead[i] = scaled < -3 ? -3 : scaled > 3 ? 3 : scaled;
      }
    }
  }

  const valuations = new Map<PlayerId, Valuation>();

  for (let i = 0; i < raw.length; i++) {
    const r = raw[i]!;

    // `components` keeps the human-meaningful values — a percentile, a
    // probability, points — because that is what the UI and the log have to
    // render. `contributions` uses the standardised versions, because that is
    // what actually moved the board.
    const components: ScoreComponents = {
      vor_z: z.vor[i]!,
      rank_score: r.rank_score,
      scarcity: r.scarcity,
      need: r.need,
      availability: r.wait_cost,
      bye_penalty: r.bye_penalty,
      lookahead: lookaheadRaw[i]!,
    };

    const contributions: ScoreComponents = {
      vor_z: weights.vor * z.vor[i]!,
      rank_score: weights.rank * z.rank[i]!,
      scarcity: weights.scarcity * z.scarcity[i]!,
      need: weights.need * z.need[i]!,
      availability: weights.availability * z.wait[i]!,
      bye_penalty: -weights.bye * z.bye[i]! * config.bye_penalty_weight,
      lookahead: weights.lookahead * zLookahead[i]!,
    };

    const final_score =
      contributions.vor_z +
      contributions.rank_score +
      contributions.scarcity +
      contributions.need +
      contributions.availability +
      contributions.bye_penalty +
      contributions.lookahead;

    valuations.set(r.id, {
      player_key: r.id,
      proj_points: r.entry.proj_points,
      replacement_baseline: baselines.points[r.entry.position] ?? 0,
      vor: Math.round(r.vor * 10) / 10,
      scarcity_score: r.scarcity,
      need_score: need.score[r.entry.position] ?? 0,
      availability_prob: r.availability_prob,
      availability_known: r.availabilityKnown,
      bye_penalty: r.bye_penalty,
      final_score,
      components,
      contributions,
      estimated_projection: input.projections.get(r.id)?.estimated ?? true,
    });
  }

  const sortedValuations = [...valuations.values()].sort((a, b) => b.final_score - a.final_score);
  const policyOrderedValuations = recommendationPolicy
    ? [
        ...sortedValuations.filter((valuation) => playerOf(valuation.player_key)?.position !== 'QB'),
        ...sortedValuations.filter((valuation) => playerOf(valuation.player_key)?.position === 'QB'),
      ]
    : sortedValuations;
  const recommendedValuations = applySpecialistRecommendationPolicy(
    policyOrderedValuations,
    (valuation) => playerOf(valuation.player_key)?.position,
    specialistPolicy,
    5,
  );

  const top = recommendedValuations
    .map((valuation) => {
      const player = playerOf(valuation.player_key)!;
      return {
        player,
        valuation,
        headline: headlineFor(player, valuation, scarcity, need, next),
        reasons: reasonsFor(player, valuation, weights, baselines, tiers.get(valuation.player_key)),
      } satisfies Recommendation;
    });

  return {
    valuations,
    top,
    recommendation_policy: recommendationPolicy,
    scarcity,
    need,
    baselines,
    tiers,
    my_next_pick: next,
    my_pick_after: after,
  };
}

function oneQbRedraftDepthPolicy(input: {
  league: LeagueConfig;
  draft: DraftState;
  config: EngineConfig;
  available: PoolEntry[];
  need: RosterNeed;
  positionOf: (id: PlayerId) => Position | null;
}): RecommendationPolicy | null {
  const { league, draft, config, available, need, positionOf } = input;

  // Roster-aware recommendations are the only modes that should apply this
  // policy. Zeroing the need weight is the explicit opt-out used by both the
  // exact-list and best-player-available presets.
  if (config.weights.need <= 0 || league.league_type !== 'redraft') return null;

  const requiredQuarterbacks = league.starters_by_position['QB'] ?? 0;
  const hasQuarterbackFlex = league.flex_slots.some(
    (flex) => flex.count > 0 && flex.eligible.includes('QB'),
  );
  if (requiredQuarterbacks !== 1 || hasQuarterbackFlex) return null;

  const ownsQuarterback = draft.my_roster.some((id) => positionOf(id) === 'QB');
  if (!ownsQuarterback) return null;

  const openCorePositions = new Set<Position>();
  for (const position of CORE_OFFENSIVE_POSITIONS) {
    if ((need.open_starters[position] ?? 0) > 0) openCorePositions.add(position);
  }
  for (const flex of need.open_flex) {
    if (flex.count <= 0) continue;
    for (const position of flex.eligible) {
      if (CORE_OFFENSIVE_POSITIONS.includes(position)) openCorePositions.add(position);
    }
  }

  const deferredPlayerCount = available.filter((entry) => entry.position === 'QB').length;
  if (openCorePositions.size === 0 || deferredPlayerCount === 0) return null;

  return {
    reason: 'one-qb-redraft-depth',
    deferred_position: 'QB',
    open_core_positions: CORE_OFFENSIVE_POSITIONS.filter((position) => openCorePositions.has(position)),
    deferred_player_count: deferredPlayerCount,
  };
}

// ---------------------------------------------------------------------------
// Explanations (FR-27: "so the user can see *why*")
// ---------------------------------------------------------------------------

function headlineFor(
  player: Player,
  v: Valuation,
  scarcity: ScarcityState,
  need: RosterNeed,
  nextPick: number | null,
): string {
  const position = player.position;
  const pos = position ?? 'FLEX';

  // Lead with whichever component actually drove the score. A breakdown nobody
  // reads is worth less than one sentence naming the real reason.
  const drivers: [string, number][] = [
    ['value', v.contributions.vor_z],
    ['ranking', v.contributions.rank_score],
    ['scarcity', v.contributions.scarcity],
    ['need', v.contributions.need],
  ];
  drivers.sort((a, b) => b[1] - a[1]);
  const lead = drivers[0]![0];

  // Only speak to timing when ADP actually told us something. Without it,
  // `availability_prob` is 0 for "unknown", and announcing "0% to survive"
  // would dress missing data up as a certainty.
  if (v.availability_known && v.availability_prob < 0.25 && nextPick !== null) {
    return `${player.full_name} won't last to pick ${nextPick} — ${Math.round(v.availability_prob * 100)}% to survive.`;
  }
  if (lead === 'need' && position && need.unfilled.includes(position)) {
    return `${player.full_name} fills your open ${pos} slot at the best value on the board.`;
  }
  if (lead === 'scarcity' && position && scarcity.runs.includes(position)) {
    return `${pos} is running — ${player.full_name} is the best one left before the tier breaks.`;
  }
  if (lead === 'value') {
    const base = `${player.full_name} is ${v.vor > 0 ? '+' : ''}${v.vor.toFixed(0)} points over a replacement ${pos}`;

    // Say why he is ranked where he is, not just what his VOR is.
    //
    // Without this the top five reads as mis-sorted, and on a real board it
    // did: a receiver with +212 sat above a back with +218 because the roster
    // needed receivers. That ordering is correct — it is what FR-25 asks for —
    // but a headline that only ever cites VOR makes it look like a bug.
    //
    // The condition is not "is this driver big relative to VOR" — VOR
    // dominates the composite by design, so that test almost never fires.
    // It is "is this fact true and useful", which an unfilled starting slot
    // always is.
    // Prefer the roster-specific fact. "RB is running" was the first thing
    // tried and it landed on four of five recommendations at once, which is
    // noise rather than information — the roster panel already surfaces runs.
    // Open starting slots differ per player and per position, which is exactly
    // what a reader comparing two rows needs.
    const open = position ? need.open_starters[position] ?? 0 : 0;
    if (open > 0) {
      return `${base}, and you still need ${open === 1 ? `a starting ${pos}` : `${open} more starting ${pos}s`}.`;
    }
    if (position && (scarcity.tier_remaining[position] ?? 99) <= 2) {
      return `${base}, and his tier is nearly gone.`;
    }
    return `${base}.`;
  }
  return `${player.full_name} is the highest-ranked player available at a position you can use.`;
}

function reasonsFor(
  player: Player,
  v: Valuation,
  weights: EngineConfig['weights'],
  baselines: ReturnType<typeof replacementBaselines>,
  tier: number | undefined,
): string[] {
  const position = player.position;
  const pos = position ?? 'FLEX';
  const baselinePoints = position ? baselines.points[position] ?? 0 : 0;
  const baselineRank = position ? baselines.rank[position] ?? 0 : 0;
  const out: string[] = [];

  out.push(
    `VOR (est.) ${v.vor >= 0 ? '+' : ''}${v.vor.toFixed(1)} — ${v.proj_points.toFixed(0)} projected vs ${baselinePoints.toFixed(0)} for replacement ${pos}${
      baselineRank ? ` (${baselines.method} baseline at ${pos}${baselineRank})` : ''
    }.`,
  );
  out.push(
    `Ranking percentile ${(v.components.rank_score * 100).toFixed(0)}%${tier ? `, tier ${tier}` : ''}.`,
  );
  if (v.scarcity_score > 0.1) {
    out.push(`${pos} scarcity ${(v.scarcity_score * 100).toFixed(0)}% — the position is draining faster than the board.`);
  }
  if (v.need_score > 0) {
    out.push(`Roster need ${(v.need_score * 100).toFixed(0)}% for ${pos}.`);
  }
  // Roster fit is the *penalty* half of need: how much less he is worth to your
  // lineup than to an empty one. It is zero while you still have a slot open at
  // his position, so it only ever appears once that stops being true.
  if (v.components.need < -1) {
    out.push(
      `Roster fit ${v.components.need.toFixed(0)} pts — your ${pos} slots are filled, so he is worth less to you than his raw value suggests.`,
    );
  }
  if (v.availability_known) {
    const verb = v.availability_prob >= 0.6 ? 'likely still there' : 'may be gone';
    const timing =
      v.components.availability > 1
        ? `, so waiting would cost about ${v.components.availability.toFixed(0)} pts`
        : ' — waiting costs you little here';
    out.push(
      `${Math.round(v.availability_prob * 100)}% to survive to your next pick — ${verb}${timing}.`,
    );
  }
  if (v.bye_penalty > 0) {
    out.push(`Bye-week conflict: week ${player.bye_week} would leave you short at ${pos}.`);
  }
  out.push(
    `Weights — value ${weights.vor}, ranking ${weights.rank}, scarcity ${weights.scarcity}, need ${weights.need}, availability ${weights.availability}, bye ${weights.bye}.`,
  );
  return out;
}

// ---------------------------------------------------------------------------
// Reproducibility (§8: "log the component values for each of the top 5")
// ---------------------------------------------------------------------------

export interface RecommendationLogEntry {
  ts: string;
  draft_id: string;
  pick_no: number;
  player_key: PlayerId;
  player_name: string;
  final_score: number;
  components: ScoreComponents;
  contributions: ScoreComponents;
  baseline_method: string;
  weights: EngineConfig['weights'];
}

/**
 * Serialise the top 5 as JSONL for post-draft review. The UI writes these to
 * IndexedDB and offers a download — on a hosted tool there is no server-side
 * `data/logs/` to write to (FR-64), so the log lives on the user's device and
 * leaves only if they export it.
 */
export function logEntries(
  output: EngineOutput,
  draft: DraftState,
  config: EngineConfig,
): RecommendationLogEntry[] {
  const ts = new Date().toISOString();
  return output.top.map((rec) => ({
    ts,
    draft_id: draft.draft_id,
    pick_no: draft.current_pick_no,
    player_key: rec.valuation.player_key,
    player_name: rec.player.full_name,
    final_score: rec.valuation.final_score,
    components: rec.valuation.components,
    contributions: rec.valuation.contributions,
    baseline_method: config.baseline_method,
    weights: config.weights,
  }));
}

// ---------------------------------------------------------------------------

const mean = (xs: number[]): number => (xs.length === 0 ? 0 : xs.reduce((a, x) => a + x, 0) / xs.length);

const stdev = (xs: number[], m: number): number =>
  xs.length === 0 ? 0 : Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / xs.length);

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * Put a feature on a unit-variance scale so that a weight means a share of
 * influence.
 *
 * This is the fix for a bug that was invisible because it lived in the *units*
 * rather than in any line of logic. VOR was z-scored (spread ≈ 1 standard
 * deviation) while every other feature was a probability or a percentile
 * (spread ≈ 0.05–0.29), so multiplying them by comparable-looking weights did
 * not make them comparable. Measured against a realistic board, the shipped
 * "Value 0.35" slider was carrying ~74% of the actual influence and "Roster
 * need 0.15" was carrying under 2%. Every number in the settings panel was
 * wrong, and no amount of tuning them could have helped, because the sliders
 * were not denominated in the same thing.
 *
 * Two details matter:
 *
 *  - **Zero variance contributes nothing.** A feature that says the same thing
 *    about every player cannot rank anybody, and dividing by its zero spread
 *    would turn rounding noise into a confident ordering. This is the same trap
 *    as a zero-variance R², and the same answer: return nothing rather than
 *    something arbitrary.
 *  - **Clipped to ±3.** Sparse features — a bye conflict that affects four
 *    players out of three hundred — have a tiny spread, so the few non-zero
 *    values standardise to enormous z-scores and a 0.03 weight starts
 *    outvoting a 0.35 one. Clipping bounds any single term at three times its
 *    weight, which keeps a rare event important without letting it take over.
 */
function standardize(values: number[]): number[] {
  const m = mean(values);
  const sd = stdev(values, m);
  if (sd === 0) return values.map(() => 0);
  return values.map((v) => {
    const scaled = (v - m) / sd;
    return scaled < -3 ? -3 : scaled > 3 ? 3 : scaled;
  });
}
